import { Box, Divider } from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PatientList from "../../components/patient/PatientList.tsx";
import SelectedPatientMainBody from "../../components/patient/SelectedPatientMainBody";
import SelectedPatientMetadata from "../../components/patient/SelectedPatientMetadata";
import {
  PatientContext,
  PatientDispatchContext,
} from "../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext";
import { useFetch } from "../../services/hooks/useFetch";
import { projectsPrefix } from "../../services/ProjectsServices";

export default function Patients({
  patientMaxMode,
  setPatientMaxMode,
}: {
  patientMaxMode: boolean;
  setPatientMaxMode: (newPatientMaxMode: boolean) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const patientId = searchParams.get("patientId");
  const projectId = searchParams.get("projectId");
  const { patientSummaries = [] } = useContext(ProjectContext)!;
  const patient = useContext(PatientContext)!;
  const dispatch = useContext(PatientDispatchContext);

  const [patientToLoad, setPatientToLoad] = useState<
    InternalPatientSummary | undefined
  >(
    patientSummaries.find((patient) => patient.id === patientId) ||
      patientSummaries[0],
  );

  const {
    fetch: getPatient,
    isLoading,
    status,
    reset,
  } = useFetch<PatientDTO>();

  const patientIndex = patientSummaries.findIndex(
    (sum) => sum.id === patient.id,
  );

  const goBackToOverview = useCallback(() => {
    searchParams.delete("patientId");
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!patientSummaries || patientSummaries.length === 0) {
      return;
    }

    if (!patientToLoad && patientId) {
      setPatientToLoad(patientSummaries.find((sum) => sum.id === patientId));
      return;
    }
    const isPatientIncluded = patientSummaries.some(
      (patient) => patient.id === patientToLoad?.id,
    );
    if (!isPatientIncluded) {
      setPatientToLoad(patientSummaries[0]);
    }
  }, [projectId, patientId, patientToLoad, patientSummaries]);

  useEffect(() => {
    if (!patientToLoad || (patient && patient.id === patientToLoad.id)) {
      return;
    }

    if (!isLoading && status === null) {
      // set id to undefined temporarily to reset child data fetch calls
      dispatch({
        type: "update",
        patient: { ...patientToLoad, id: undefined },
      });
      void getPatient(
        `${projectsPrefix}/${projectId}/patients/${patientToLoad.id}`,
        {
          postResponseAction: (data) => {
            if (!data) return;
            dispatch({
              type: "update",
              patient: data,
            });
            searchParams.set("patientId", patientToLoad.id);
            setSearchParams(searchParams);
          },
        },
      );
    }
  }, [
    dispatch,
    getPatient,
    isLoading,
    patient,
    patient.id,
    patientToLoad,
    projectId,
    searchParams,
    setSearchParams,
    status,
  ]);

  useEffect(() => {
    if (!isLoading && status !== null && status !== 200) {
      goBackToOverview();
    }
  }, [goBackToOverview, isLoading, status]);

  function onPatientSelected(patientSelected: InternalPatientSummary) {
    setPatientToLoad(patientSelected);
    dispatch({ type: "delete" });
    reset();
  }

  function navigatePatients(isNext: boolean) {
    let newIndex = patientIndex;
    if (isNext && patientIndex !== patientSummaries.length - 1) {
      newIndex += 1;
    } else if (!isNext && patientIndex > 0) {
      newIndex -= 1;
    }
    const newSummary = patientSummaries[newIndex];
    onPatientSelected(newSummary);
  }

  return (
    <div className="patients-box">
      {!patientMaxMode && (
        <div className="patient-list">
          <PatientList
            patientSelected={patient}
            onPatientSelected={onPatientSelected}
          />
          <Divider orientation={"vertical"} flexItem />
        </div>
      )}
      <Box className="selected-patient" sx={{ bgcolor: "grey.50" }}>
        <SelectedPatientMetadata
          patientMaxMode={patientMaxMode}
          setPatientMaxMode={setPatientMaxMode}
          prevDisabled={patientIndex === 0}
          nextDisabled={patientIndex === patientSummaries.length - 1}
          goPrevPatient={() => navigatePatients(false)}
          goNextPatient={() => navigatePatients(true)}
        />
        <Divider flexItem />
        <SelectedPatientMainBody />
      </Box>
    </div>
  );
}
